// src/main/FloorUnit.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Card,
    CardContent,
    CardActions,
    Tabs,
    Tab,
    Avatar,
    LinearProgress,
    Stack,
    Alert,
    Snackbar,
    FormControlLabel,
    Switch,
    Rating,
    Stepper,
    Step,
    StepLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Breadcrumbs,
    Link,
    Fab,
    Zoom,
    Badge,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    Filter as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    Apartment as ApartmentIcon,
    Home as HomeIcon,
    LocationOn,
    Business,
    FormatListNumbered,
    PictureAsPdf,
    TableChart,
    GetApp,
    Print,
    MeetingRoom,
    Build,
    CheckCircle,
    Cancel,
    People,
    AttachMoney,
    TrendingUp,
    TrendingDown,
    MoreVert,
    Dashboard as DashboardIcon,
    List as ListIcon,
    Store as StoreIcon,
    Phone,
    Email,
    Person,
    Assignment,
    Map as MapIcon,
    ViewModule,
    ViewList,
    ExpandMore,
    NavigateNext,
    CheckBox,
    RadioButtonChecked,
    BedroomParent,
    Bathtub,
    SquareFoot,
    Kitchen,
    LocalParking,
    Elevator,
    Security,
    Wifi,
    AcUnit,
    LocalLaundryService,
    Pool,
    AddCircleOutlineOutlined
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIPropertyList,
    APIApartmentList,
    APIFloorUnitList,
    APIFloorUnitStore,
    APIFloorUnitUpdate,
    APIFloorUnitDelete,
    APIFloorUnitExportExcel,
    APIFloorUnitExportPDF,
    APIFloorUnitPrint,
    APIFloorUnitStats,
    APIFloorUnitMap
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const FloorUnit = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('list');
    const [statistics, setStatistics] = useState(null);
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState('');
    const [floorMap, setFloorMap] = useState({});

    // Use ref to track if component is mounted
    const isMounted = useRef(true);

    // Form data for FloorUnit
    const [formData, setFormData] = useState({
        property_id: '',
        floor_name: '',
        unit_name: '',
        monthly_rent_price: '',
        occupancy_status: 'vacant',
        security_deposit: '',
        maintenance_fee: '',
        water_fee: '',
        electricity_fee: '',
        garbage_fee: '',
        amenities: [],
        notes: ''
    });

    const [filters, setFilters] = useState({
        property_id: '',
        floor_name: '',
        occupancy_status: '',
        min_rent: '',
        max_rent: ''
    });

    const [errors, setErrors] = useState({
        property_id: false,
        floor_name: false,
        unit_name: false,
        monthly_rent_price: false
    });

    // Amenities options
    const amenitiesOptions = [
        { id: 'parking', name: 'Parking', icon: <LocalParking /> },
        { id: 'elevator', name: 'Elevator', icon: <Elevator /> },
        { id: 'security', name: '24/7 Security', icon: <Security /> },
        { id: 'wifi', name: 'WiFi', icon: <Wifi /> },
        { id: 'ac', name: 'Air Conditioning', icon: <AcUnit /> },
        { id: 'laundry', name: 'Laundry', icon: <LocalLaundryService /> },
        { id: 'pool', name: 'Swimming Pool', icon: <Pool /> },
        { id: 'gym', name: 'Gym', icon: <TrendingUp /> }
    ];

    // Occupancy status options
    const occupancyStatusOptions = [
        { id: 'vacant', name: 'Vacant', color: 'success', icon: <CheckBox /> },
        { id: 'occupied', name: 'Occupied', color: 'warning', icon: <MeetingRoom /> },
        { id: 'maintenance', name: 'Maintenance', color: 'error', icon: <Build /> }
    ];

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchProperties();
        fetchFloorUnits();
        fetchStatistics();

        return () => {
            isMounted.current = false;
        };
    }, [showToast, selectedProperty]);

    const fetchProperties = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APIPropertyList);
            if (result.success && isMounted.current) {
                setProperties(Array.isArray(result.data) ? result.data : []);
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
        }
    };

    const fetchFloorUnits = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const params = {};
            if (selectedProperty) params.property_id = selectedProperty;
            
            const result = await apiService.list(userBranchId, APIFloorUnitList, params);

            if (result.success && isMounted.current) {
                const floorUnits = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: floorUnits
                }));
                generateFloorMap(floorUnits);
            } else if (isMounted.current) {
                setError(result.message);
            }
        } catch (error) {
            if (isMounted.current) {
                setError("Failed to load data");
            }
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const generateFloorMap = (floorUnits) => {
        const map = {};
        floorUnits.forEach(unit => {
            if (!map[unit.floor_name]) {
                map[unit.floor_name] = [];
            }
            map[unit.floor_name].push(unit);
        });
        Object.keys(map).forEach(floor => {
            map[floor].sort((a, b) => {
                const aNum = a.unit_name?.match(/\d+/);
                const bNum = b.unit_name?.match(/\d+/);
                return (aNum ? parseInt(aNum[0]) : 0) - (bNum ? parseInt(bNum[0]) : 0);
            });
        });
        setFloorMap(map);
    };

    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const params = selectedProperty ? { property_id: selectedProperty } : {};
            const result = await apiService.listStats(userBranchId, APIFloorUnitStats, params);
            if (result.success && result.data && isMounted.current) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };
    
    const handlePrint = async (record) => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print floor unit details", "error");
            return;
        }
        
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));

            const result = await apiService.print(APIFloorUnitPrint, formDataToSend);

            if (result && result.success && isMounted.current) {
                showToast("Success", "PDF file generated successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            if (isMounted.current) {
                showToast("Error", "Failed to generate PDF file", "error");
            }
        }
    };
    
    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export floor unit data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            if (selectedProperty) {
                formDataToSend.append('property_id', selectedProperty);
            }
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToExcel(APIFloorUnitExportExcel, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export Excel file", "error");
            }
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export floor unit data", "error");
            handleExportClose();
            return;
        }
        
        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            if (selectedProperty) {
                formDataToSend.append('property_id', selectedProperty);
            }
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.exportToPDF(APIFloorUnitExportPDF, formDataToSend);

            if (result && result.success && isMounted.current) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else if (isMounted.current) {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            if (isMounted.current) {
                showToast("Error", error.message || "Failed to export PDF file", "error");
            }
        }
    };

    // Filtered data with useMemo
    const filteredData = useMemo(() => {
        return pageState.pageData.filter(record => {
            const matchesSearch = searchTerm === '' ||
                record.unit_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.property_name?.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesProperty = !filters.property_id || 
                record.property_id?.toString() === filters.property_id;

            const matchesFloor = !filters.floor_name || 
                record.floor_name?.toString() === filters.floor_name;

            const matchesStatus = !filters.occupancy_status || 
                record.occupancy_status === filters.occupancy_status;

            const matchesMinRent = !filters.min_rent ||
                (record.monthly_rent_price && record.monthly_rent_price >= parseFloat(filters.min_rent));

            const matchesMaxRent = !filters.max_rent ||
                (record.monthly_rent_price && record.monthly_rent_price <= parseFloat(filters.max_rent));

            return matchesSearch && matchesProperty && matchesFloor && 
                   matchesStatus && matchesMinRent && matchesMaxRent;
        });
    }, [pageState.pageData, searchTerm, filters]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add floor units", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            property_id: selectedProperty || '',
            floor_name: '',
            unit_name: '',
            monthly_rent_price: '',
            occupancy_status: 'vacant',
            security_deposit: '',
            maintenance_fee: '',
            water_fee: '',
            electricity_fee: '',
            garbage_fee: '',
            amenities: [],
            notes: ''
        });
        setErrors({
            property_id: false,
            floor_name: false,
            unit_name: false,
            monthly_rent_price: false
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit floor units", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            property_id: record.property_id || '',
            floor_name: record.floor_name || '',
            unit_name: record.unit_name || '',
            monthly_rent_price: record.monthly_rent_price || '',
            occupancy_status: record.occupancy_status || 'vacant',
            security_deposit: record.security_deposit || '',
            maintenance_fee: record.maintenance_fee || '',
            water_fee: record.water_fee || '',
            electricity_fee: record.electricity_fee || '',
            garbage_fee: record.garbage_fee || '',
            amenities: record.amenities || [],
            notes: record.notes || ''
        });
        setOpenAdd(true);
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete floor units", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAmenitiesChange = (amenityId) => {
        setFormData(prev => {
            const currentAmenities = prev.amenities || [];
            if (currentAmenities.includes(amenityId)) {
                return {
                    ...prev,
                    amenities: currentAmenities.filter(a => a !== amenityId)
                };
            } else {
                return {
                    ...prev,
                    amenities: [...currentAmenities, amenityId]
                };
            }
        });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            property_id: '',
            floor_name: '',
            occupancy_status: '',
            min_rent: '',
            max_rent: ''
        });
        setSearchTerm('');
        setSelectedProperty('');
        handleCloseOptions();
        fetchFloorUnits();
        fetchStatistics();
    };

    const applyPropertyFilter = () => {
        fetchFloorUnits();
        fetchStatistics();
        handleCloseOptions();
    };

    const validateForm = () => {
        if (!formData.property_id) {
            showToast("Validation Error", "Please select a property", "warning");
            return false;
        }
        if (!formData.floor_name) {
            showToast("Validation Error", "Floor number is required", "warning");
            return false;
        }
        if (!formData.unit_name) {
            showToast("Validation Error", "Unit name is required", "warning");
            return false;
        }
        if (!formData.monthly_rent_price || formData.monthly_rent_price <= 0) {
            showToast("Validation Error", "Monthly rent price is required and must be greater than 0", "warning");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add floor units", "error");
            return;
        }
        
        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update floor units", "error");
            return;
        }
        
        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete floor units", "error");
            return;
        }
        
        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('property_id', formData.property_id);
            formDataToSend.append('floor_name', formData.floor_name);
            formDataToSend.append('unit_name', formData.unit_name);
            formDataToSend.append('monthly_rent_price', formData.monthly_rent_price);
            formDataToSend.append('occupancy_status', formData.occupancy_status);
            formDataToSend.append('security_deposit', formData.security_deposit || 0);
            formDataToSend.append('maintenance_fee', formData.maintenance_fee || 0);
            formDataToSend.append('water_fee', formData.water_fee || 0);
            formDataToSend.append('electricity_fee', formData.electricity_fee || 0);
            formDataToSend.append('garbage_fee', formData.garbage_fee || 0);
            formDataToSend.append('amenities', JSON.stringify(formData.amenities || []));
            formDataToSend.append('notes', formData.notes || '');
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APIFloorUnitStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APIFloorUnitUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APIFloorUnitDelete, formDataToSend);
            }

            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchFloorUnits();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
            if (isMounted.current) {
                showToast("Error", error.response?.data?.message || "Operation failed", "error");
            }
        }
    };

    const getStatusColor = (status) => {
        const statusMap = {
            'vacant': 'success',
            'occupied': 'warning',
            'maintenance': 'error'
        };
        return statusMap[status] || 'default';
    };

    const getStatusIcon = (status) => {
        const statusMap = {
            'vacant': <CheckBox />,
            'occupied': <MeetingRoom />,
            'maintenance': <Build />
        };
        return statusMap[status] || <ApartmentIcon />;
    };

    const getStatusText = (status) => {
        const option = occupancyStatusOptions.find(opt => opt.id === status);
        return option ? option.name : status;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const renderFloorMap = () => {
        if (viewMode !== 'map') return null;
        
        if (Object.keys(floorMap).length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <MapIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No floor data available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Add floor units to see the visual map
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ p: 2 }}>
                {Object.keys(floorMap).sort((a, b) => parseInt(b) - parseInt(a)).map(floor => (
                    <Accordion key={floor} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h6">
                                    Floor {floor}
                                </Typography>
                                <Chip 
                                    label={`${floorMap[floor].length} Units`} 
                                    size="small" 
                                    color="primary" 
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {floorMap[floor].map((unit) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={unit.id}>
                                        <Card 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': { boxShadow: 6 },
                                                borderLeft: `4px solid ${unit.occupancy_status === 'occupied' ? theme.palette.warning.main : 
                                                              unit.occupancy_status === 'maintenance' ? theme.palette.error.main : 
                                                              theme.palette.success.main}`
                                            }}
                                            onClick={() => handleOpenView(unit)}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Typography variant="h6" color="primary">
                                                        {unit.unit_name}
                                                    </Typography>
                                                    <Chip
                                                        icon={getStatusIcon(unit.occupancy_status)}
                                                        label={getStatusText(unit.occupancy_status)}
                                                        color={getStatusColor(unit.occupancy_status)}
                                                        size="small"
                                                    />
                                                </Box>
                                                <Typography variant="h6" sx={{ mt: 1, color: theme.palette.success.main }}>
                                                    {formatCurrency(unit.monthly_rent_price)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    / month
                                                </Typography>
                                            </CardContent>
                                            <CardActions>
                                                <Button size="small" onClick={(e) => { e.stopPropagation(); handleOpenEdit(unit); }}>
                                                    <EditIcon fontSize="small" /> Edit
                                                </Button>
                                                <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); handleOpenDelete(unit); }}>
                                                    <Delete fontSize="small" /> Delete
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        );
    };

    const renderGridView = () => {
        if (viewMode !== 'grid') return null;
        
        const currentData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        
        if (currentData.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <MeetingRoom sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No floor units found
                    </Typography>
                </Box>
            );
        }

        return (
            <Grid container spacing={2} sx={{ p: 2 }}>
                {currentData.map((record) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={record.id}>
                        <Card sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': { boxShadow: 6 }
                        }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Chip 
                                        label={`Floor ${record.floor_name}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                    <Chip
                                        icon={getStatusIcon(record.occupancy_status)}
                                        label={getStatusText(record.occupancy_status)}
                                        color={getStatusColor(record.occupancy_status)}
                                        size="small"
                                    />
                                </Box>
                                <Typography variant="h5" component="div" gutterBottom>
                                    {record.unit_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    {record.property_name}
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 2, color: theme.palette.success.main }}>
                                    {formatCurrency(record.monthly_rent_price)}
                                    <Typography variant="caption" color="text.secondary">
                                        /month
                                    </Typography>
                                </Typography>
                                {record.security_deposit > 0 && (
                                    <Typography variant="caption" display="block" color="text.secondary">
                                        Deposit: {formatCurrency(record.security_deposit)}
                                    </Typography>
                                )}
                            </CardContent>
                            <CardActions>
                                <Button size="small" onClick={() => handleOpenView(record)}>
                                    <ViewIcon fontSize="small" /> View
                                </Button>
                                <Button size="small" onClick={() => handleOpenEdit(record)}>
                                    <EditIcon fontSize="small" /> Edit
                                </Button>
                                <Button size="small" color="error" onClick={() => handleOpenDelete(record)}>
                                    <Delete fontSize="small" /> Delete
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Floor Unit Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchFloorUnits}
                title="Unable to Load Floor Unit Data"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            {statistics && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Units</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                        {statistics.total_units || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                    <MeetingRoom sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Occupied Units</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                        {statistics.occupied_units || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 56, height: 56 }}>
                                    <People sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Vacant Units</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                        {statistics.vacant_units || 0}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                    <CheckBox sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="h6" color="text.secondary">Total Revenue</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                        {formatCurrency(statistics.total_monthly_revenue || 0)}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light, width: 56, height: 56 }}>
                                    <AttachMoney sx={{ fontSize: 32 }} />
                                </Avatar>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Floor Unit ({recordsCount(pageState.pageData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Select Property</InputLabel>
                            <Select
                                value={selectedProperty}
                                onChange={(e) => {
                                    setSelectedProperty(e.target.value);
                                    applyPropertyFilter();
                                }}
                                label="Select Property"
                            >
                                <MenuItem value="">All Properties</MenuItem>
                                {properties.map((property) => (
                                    <MenuItem key={property.id} value={property.id}>
                                        {property.property_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Add Unit
                            </Button>
                        )}
                        
                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="info"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Filter
                            </Button>
                        )}
                        
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                fetchFloorUnits();
                                fetchStatistics();
                            }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Refresh 
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            size="small"
                        >
                            <ToggleButton value="list">
                                <ViewList />
                            </ToggleButton>
                            <ToggleButton value="grid">
                                <ViewModule />
                            </ToggleButton>
                            <ToggleButton value="map">
                                <MapIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Export Data
                            </Button>
                        )}
                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search units..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.property_id || filters.floor_name || filters.occupancy_status || 
                  filters.min_rent || filters.max_rent) && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.property_id && (
                            <Chip
                                label={`Property: ${properties.find(p => p.id.toString() === filters.property_id)?.property_name || filters.property_id}`}
                                onDelete={() => setFilters(prev => ({ ...prev, property_id: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.floor_name && (
                            <Chip
                                label={`Floor: ${filters.floor_name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, floor_name: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.occupancy_status && (
                            <Chip
                                label={`Status: ${getStatusText(filters.occupancy_status)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, occupancy_status: '' }))}
                                color={getStatusColor(filters.occupancy_status)}
                                variant="outlined"
                            />
                        )}
                        {filters.min_rent && (
                            <Chip
                                label={`Min Rent: ${formatCurrency(filters.min_rent)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, min_rent: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.max_rent && (
                            <Chip
                                label={`Max Rent: ${formatCurrency(filters.max_rent)}`}
                                onDelete={() => setFilters(prev => ({ ...prev, max_rent: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* List View */}
                {viewMode === 'list' && (
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Property</TableCell>
                                        <TableCell>Floor</TableCell>
                                        <TableCell>Unit Name</TableCell>
                                        <TableCell>Monthly Rent (UGX)</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Fees</TableCell>
                                        <TableCell>Created Date</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                        <TableRow
                                            key={record.id || index}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                    {record.property_name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`Floor ${record.floor_name}`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {record.unit_name}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                                    {formatCurrency(record.monthly_rent_price)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(record.occupancy_status)}
                                                    label={getStatusText(record.occupancy_status)}
                                                    color={getStatusColor(record.occupancy_status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={`Deposit: ${formatCurrency(record.security_deposit || 0)}\nMaintenance: ${formatCurrency(record.maintenance_fee || 0)}`}>
                                                    <Typography variant="caption">
                                                        Deposit: {formatCurrency(record.security_deposit || 0)}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{formatCreatedAtDate(record.created_at)}</TableCell>
                                            <TableCell align="center">
                                                {hasPermission('VIEW') && (
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleOpenView(record)}
                                                            color="primary"
                                                        >
                                                            <ViewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {hasPermission('PRINT') && (
                                                    <Tooltip title="Print">
                                                        <IconButton
                                                            onClick={() => handlePrint(record)}
                                                            color="info"
                                                            size="small"
                                                        >
                                                            <Print />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {hasPermission('EDIT') && (
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleOpenEdit(record)}
                                                            color="secondary"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {hasPermission('DELETE') && (
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleOpenDelete(record)}
                                                            color="error"
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />
                    </Paper>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && renderGridView()}

                {/* Map View */}
                {viewMode === 'map' && renderFloorMap()}

                {filteredData.length === 0 && viewMode === 'list' && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 8,
                        color: 'text.secondary'
                    }}>
                        <MeetingRoom sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h6">No floor units found</Typography>
                        <Typography variant="body2">Try adjusting your search query</Typography>
                    </Box>
                )}
            </motion.div>

            {/* Filter Options Dialog - Prevent click outside */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                        >
                            Show All Units
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter Floor Units
                        </Typography>

                        <Box>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Property</InputLabel>
                                    <Select
                                        name="property_id"
                                        value={filters.property_id}
                                        onChange={handleFilterChange}
                                        label="Property"
                                    >
                                        <MenuItem value="">All Properties</MenuItem>
                                        {properties.map((property) => (
                                            <MenuItem key={property.id} value={property.id}>
                                                {property.property_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Floor Number"
                                    name="floor_name"
                                    type="number"
                                    value={filters.floor_name}
                                    onChange={handleFilterChange}
                                />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Occupancy Status</InputLabel>
                                    <Select
                                        name="occupancy_status"
                                        value={filters.occupancy_status}
                                        onChange={handleFilterChange}
                                        label="Occupancy Status"
                                    >
                                        <MenuItem value="">All Status</MenuItem>
                                        {occupancyStatusOptions.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Min Rent (UGX)"
                                    name="min_rent"
                                    type="number"
                                    value={filters.min_rent}
                                    onChange={handleFilterChange}
                                />
                            </Grid><br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Max Rent (UGX)"
                                    name="max_rent"
                                    type="number"
                                    value={filters.max_rent}
                                    onChange={handleFilterChange}
                                />
                            </Grid>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyPropertyFilter} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Floor Unit Dialog - Prevent click outside */}
            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdd();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New Floor Unit" : "Edit Floor Unit"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                                    Unit Information
                                </Typography>
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Property *</InputLabel>
                                    <Select
                                        name="property_id"
                                        value={formData.property_id}
                                        onChange={handleInputChange}
                                        label="Property *"
                                    >
                                        <MenuItem value="">Select Property</MenuItem>
                                        {properties.map((property) => (
                                            <MenuItem key={property.id} value={property.id}>
                                                {property.property_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Floor Name *"
                                    name="floor_name"
                                    value={formData.floor_name}
                                    onChange={handleInputChange}
                                    required
                                    inputProps={{ min: 0 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FormatListNumbered />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Unit Name *"
                                    name="unit_name"
                                    value={formData.unit_name}
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <HomeIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Occupancy Status</InputLabel>
                                    <Select
                                        name="occupancy_status"
                                        value={formData.occupancy_status}
                                        onChange={handleInputChange}
                                        label="Occupancy Status"
                                    >
                                        {occupancyStatusOptions.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {item.icon}
                                                    {item.name}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1, mt: 2 }}>
                                    Pricing Information (UGX)
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Monthly Rent Price *"
                                    name="monthly_rent_price"
                                    type="number"
                                    value={formData.monthly_rent_price}
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AttachMoney />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Security Deposit"
                                    name="security_deposit"
                                    type="number"
                                    value={formData.security_deposit}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AttachMoney />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Maintenance Fee"
                                    name="maintenance_fee"
                                    type="number"
                                    value={formData.maintenance_fee}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Build />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Water Fee"
                                    name="water_fee"
                                    type="number"
                                    value={formData.water_fee}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Electricity Fee"
                                    name="electricity_fee"
                                    type="number"
                                    value={formData.electricity_fee}
                                    onChange={handleInputChange}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1, mt: 2 }}>
                                    Amenities
                                </Typography>
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    {amenitiesOptions.map((amenity) => (
                                        <FormControlLabel
                                            key={amenity.id}
                                            control={
                                                <Switch
                                                    checked={formData.amenities?.includes(amenity.id) || false}
                                                    onChange={() => handleAmenitiesChange(amenity.id)}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {amenity.icon}
                                                    {amenity.name}
                                                </Box>
                                            }
                                        />
                                    ))}
                                </Box>
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={4}
                                    placeholder="Additional information about this unit..."
                                />
                            </Grid>
                        </Box>

                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button color="error" onClick={handleCloseAdd} variant="contained">
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained">
                                {postStatus === "ADD" ? "Save Unit" : "Update Unit"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog - Prevent click outside */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Floor Unit Details</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Box>
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        Unit Information
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Property:</b> {selectedRecord.property_name}</Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Floor Number:</b> {selectedRecord.floor_name}</Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Unit Name:</b> {selectedRecord.unit_name}</Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2"><b>Occupancy Status:</b> 
                                        <Chip
                                            icon={getStatusIcon(selectedRecord.occupancy_status)}
                                            label={getStatusText(selectedRecord.occupancy_status)}
                                            color={getStatusColor(selectedRecord.occupancy_status)}
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    </Typography>
                                </Grid>
                                <br/>

                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                                        Pricing Information
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2">
                                        <b>Monthly Rent:</b> {formatCurrency(selectedRecord.monthly_rent_price)}
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2">
                                        <b>Security Deposit:</b> {formatCurrency(selectedRecord.security_deposit || 0)}
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2">
                                        <b>Maintenance Fee:</b> {formatCurrency(selectedRecord.maintenance_fee || 0)}
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2">
                                        <b>Water Fee:</b> {formatCurrency(selectedRecord.water_fee || 0)}
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2">
                                        <b>Electricity Fee:</b> {formatCurrency(selectedRecord.electricity_fee || 0)}
                                    </Typography>
                                </Grid>
                                <br/>

                                {selectedRecord.amenities && selectedRecord.amenities.length > 0 && (
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                                                Amenities
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {selectedRecord.amenities.map((amenityId) => {
                                                    const amenity = amenitiesOptions.find(a => a.id === amenityId);
                                                    return amenity ? (
                                                        <Chip
                                                            key={amenityId}
                                                            icon={amenity.icon}
                                                            label={amenity.name}
                                                            variant="outlined"
                                                        />
                                                    ) : null;
                                                })}
                                            </Box>
                                        </Grid>
                                    </>
                                )}
                                <br/>
                                {selectedRecord.notes && (
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                                                Notes
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2">{selectedRecord.notes}</Typography>
                                        </Grid>
                                    </>
                                )}
                                <br/>
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary" gutterBottom sx={{ mt: 2 }}>
                                        System Information
                                    </Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Created Date:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                                </Grid>
                                <br/>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}</Typography>
                                </Grid>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">
                        Close
                    </Button>
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrint(selectedRecord)} color="info" variant="contained">
                            <Print sx={{ mr: 1 }} /> Print
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog - Prevent click outside */}
            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDelete();
                    }
                }}
                TransitionComponent={Transition}
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Floor Unit</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this floor unit?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.unit_name} - Floor {selectedRecord.floor_name}
                            <Box sx={{ mt: 1 }}>
                                <Chip
                                    icon={<ApartmentIcon />}
                                    label={selectedRecord.property_name}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                                <Chip
                                    icon={getStatusIcon(selectedRecord.occupancy_status)}
                                    label={getStatusText(selectedRecord.occupancy_status)}
                                    color={getStatusColor(selectedRecord.occupancy_status)}
                                    size="small"
                                    sx={{ ml: 1 }}
                                />
                            </Box>
                        </Typography>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>
                        Warning: This action cannot be undone!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default FloorUnit;